import React from "react";
import { Button } from "@/components/ui/button";
import { LinkWrapper } from "@/components/LinkWrapper";
import { cn } from "@/lib/utils";

type Tone = "onLight" | "onPrimary";
type Variant = "solid" | "outline";

interface PosterBoldPillButtonProps {
  /** Destination URL. If undefined or empty the button doesn't render — guards
   *  against Strapi-driven content where the link may be missing. */
  href?: string;
  tone?: Tone;
  variant?: Variant;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

// Resolve the right Tailwind classes for each (tone, variant) combo. Outline
// uses a 2px border and a transparent background so it pairs with a solid
// primary pill without competing.
const styleFor = (tone: Tone, variant: Variant): string => {
  if (variant === "solid") {
    return tone === "onPrimary"
      ? "bg-background text-foreground hover:bg-background/90"
      : ""; // default Button "default" variant already gives the onLight solid look
  }
  // outline
  return tone === "onPrimary"
    ? "bg-transparent border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
    : "bg-transparent border-2 border-foreground text-foreground hover:bg-foreground hover:text-background";
};

export const PosterBoldPillButton: React.FC<PosterBoldPillButtonProps> = ({
  href,
  tone = "onLight",
  variant = "solid",
  className,
  children,
  onClick,
}) => {
  if (!href) return null;
  return (
    <Button
      variant="default"
      websiteStyle="posterBold"
      asChild
      className={cn(styleFor(tone, variant), className)}
    >
      <LinkWrapper to={href} onClick={onClick}>{children}</LinkWrapper>
    </Button>
  );
};
